import * as React from 'react';

import {
  createStyles,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  WithStyles,
  withStyles
} from '@material-ui/core';

import { LegendProps } from '@nivo/legends';
import { PieDatum, ResponsivePie } from '@nivo/pie';
import numeral from 'numeral';
import getChartTheme from './getChartTheme';
import InfoBubble from './InfoBubble';

const styles = (theme: Theme) =>
  createStyles({
    pieChart: {
      position: 'relative',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(6),
      height: 220,
      width: '100%'
    },
    title: {
      fontSize: '1em'
    }
  });

const Pie: React.FC<{ data: PieDatum[]; sum: number }> = ({ data, sum }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const chartTheme = getChartTheme(isSmall, theme);

  const dataMapped: PieDatum[] = data
    .filter((e) => e.value > 0)
    .map((e) => ({
      ...e,
      value: e.value / sum
    }));

  const maxTextSize = dataMapped.reduce((prev, curr): number => {
    return String(curr.id).length > prev ? String(curr.id).length : prev;
  }, 0);

  const textColor = chartTheme.labels.text.color;

  const enableRadialLabels = false;

  const legends: LegendProps[] = !enableRadialLabels
    ? [
        {
          anchor: 'top-right',
          direction: 'column',
          itemTextColor: textColor,
          justify: false,
          translateX: 30,
          itemWidth: 40 + maxTextSize * 5,
          itemHeight: 24,
          itemDirection: 'right-to-left',
          itemOpacity: 0.75,
          itemBackground: theme.palette.background.paper,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]
    : [];

  return (
    <ResponsivePie
      data={dataMapped}
      sortByValue
      margin={{ top: 10, right: 60, bottom: 10, left: 60 }}
      innerRadius={0.5}
      padAngle={0.7}
      cornerRadius={4}
      colors={{ scheme: 'pastel1' }}
      borderWidth={1}
      borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
      enableRadialLabels={enableRadialLabels}
      radialLabelsTextColor={textColor}
      radialLabelsLinkDiagonalLength={5}
      radialLabelsLinkHorizontalLength={10}
      radialLabelsLinkStrokeWidth={2}
      radialLabelsLinkColor={{ from: 'color' }}
      slicesLabelsSkipAngle={10}
      slicesLabelsTextColor={textColor}
      animate={true}
      isInteractive={true}
      sliceLabel={(data) => numeral(data.value).format('0%')}
      enableSlicesLabels={false}
      tooltip={(data) => <div>{numeral(data.value).format('0%')}</div>}
      legends={legends}
      theme={chartTheme}
    />
  );
};

const PieChart: React.FC<WithStyles<typeof styles> & {
  title: string;
  description: React.ReactNode;
  data: PieDatum[];
}> = ({ classes, title, data, description }) => {
  const sum = data.reduce((prev, curr) => curr.value + prev, 0);

  if (sum <= 0) {
    return <></>;
  }

  return (
    <Grid item xs={12} md={6}>
      <Paper className={classes.pieChart}>
        <Typography className={classes.title} gutterBottom variant="overline">
          {title.toUpperCase()}
        </Typography>

        <InfoBubble title={title} body={description} />

        <Pie data={data} sum={sum} />
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(PieChart);
